import { Button } from '@lensora/ui'
import { Check, Copy } from 'lucide-react'
import * as React from 'react'

import { apiUrl } from '@/lib/api'

// ─── Types ──────────────────────────────────────────────────────────────────

interface ConnectSnippetsProps {
  /** Live secret while the reveal modal is open, or null once dismissed. */
  secret: string | null
  keyPrefix: string
}

type SnippetId = 'claude' | 'cursor' | 'generic'

// ─── Snippet builders ────────────────────────────────────────────────────────

function tokenFor(secret: string | null, keyPrefix: string): string {
  // Masked once the secret is gone — never reconstructable, just a shape hint.
  return secret ?? `${keyPrefix}_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`
}

function claudeDesktopSnippet(secret: string | null, keyPrefix: string): string {
  const token = tokenFor(secret, keyPrefix)
  return JSON.stringify(
    {
      mcpServers: {
        lensora: {
          command: 'npx',
          args: ['-y', 'mcp-remote', apiUrl('/mcp'), '--header', `Authorization: Bearer ${token}`],
        },
      },
    },
    null,
    2
  )
}

function cursorSnippet(secret: string | null, keyPrefix: string): string {
  const token = tokenFor(secret, keyPrefix)
  return JSON.stringify(
    {
      mcpServers: {
        lensora: {
          url: apiUrl('/mcp'),
          headers: { Authorization: `Bearer ${token}` },
        },
      },
    },
    null,
    2
  )
}

function genericSnippet(secret: string | null, keyPrefix: string): string {
  const token = tokenFor(secret, keyPrefix)
  return [
    `Transport: Streamable HTTP`,
    `URL:       ${apiUrl('/mcp')}`,
    `Header:    Authorization: Bearer ${token}`,
  ].join('\n')
}

const TABS: { id: SnippetId; label: string; build: (secret: string | null, keyPrefix: string) => string }[] = [
  { id: 'claude', label: 'Claude Desktop', build: claudeDesktopSnippet },
  { id: 'cursor', label: 'Cursor', build: cursorSnippet },
  { id: 'generic', label: 'n8n / other (HTTP + Bearer)', build: genericSnippet },
]

// ─── Component ────────────────────────────────────────────────────────────────

export function ConnectSnippets({ secret, keyPrefix }: ConnectSnippetsProps) {
  const [activeTab, setActiveTab] = React.useState<SnippetId>('claude')
  const [copied, setCopied] = React.useState(false)

  const tab = TABS.find((t) => t.id === activeTab) ?? TABS[0]!
  const snippet = tab.build(secret, keyPrefix)

  function handleCopy() {
    navigator.clipboard
      .writeText(snippet)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => {})
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveTab(t.id)}
            className={`rounded-t px-3 py-1.5 text-xs font-medium transition-colors ${
              activeTab === t.id
                ? 'border-b-2 border-brand-primary text-text'
                : 'text-text-muted hover:text-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="relative">
        <pre className="max-h-64 overflow-auto rounded-md border border-border bg-surface-raised p-3 text-xs text-text">
          <code>{snippet}</code>
        </pre>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="absolute right-2 top-2"
          onClick={handleCopy}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {!secret && (
        <p className="text-xs text-text-muted">
          Placeholder shown — the real secret was only displayed once, at creation.
        </p>
      )}
    </div>
  )
}
